import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MapPin,
  Clock,
  Navigation,
  Plane,
  Building,
  Car,
  Train,
  ExternalLink,
  Download,
  ArrowRight,
  Copy,
  Check,
  Maximize2,
  Minimize2,
  Compass,
  Layers,
  Map as MapIcon,
  Route,
  Sparkles,
  Info,
} from 'lucide-react';
import { eventInfo } from '../data';

type TransitMode = 'airport' | 'metro' | 'roads';
type MapViewMode = 'live' | 'corridors';
type MapLayer = 'roadmap' | 'satellite';

interface TransitDetail {
  id: TransitMode;
  title: string;
  badge: string;
  badgeBg: string;
  badgeBorder: string;
  badgeText: string;
  icon: typeof Plane;
  iconBg: string;
  iconBorder: string;
  iconText: string;
  summary: string;
  expandedDetails: {
    route: string;
    travelTime: string;
    tips: string;
    directionsUrl: string;
  };
}

const TRANSIT_OPTIONS: TransitDetail[] = [
  {
    id: 'airport',
    title: "Mumbai Int'l Airport (T2)",
    badge: '8 km • 15–20 min',
    badgeBg: 'bg-emerald-50',
    badgeBorder: 'border-emerald-200',
    badgeText: 'text-emerald-800',
    icon: Plane,
    iconBg: 'bg-emerald-50',
    iconBorder: 'border-emerald-100',
    iconText: 'text-[#0A3D27]',
    summary: 'Direct expressway connectivity via Western Express Highway & BKC Connector.',
    expandedDetails: {
      route: 'Elevated BKC Connector bypasses suburban traffic directly into G Block.',
      travelTime: '15–20 minutes via pre-paid taxi, Uber, or Ola from Terminal 2.',
      tips: 'Dedicated ride-hailing pickup zone at T2 P4 Level. Follow signs for BKC Connector.',
      directionsUrl:
        'https://www.google.com/maps/dir/Chhatrapati+Shivaji+Maharaj+International+Airport,+Mumbai/Jio+World+Convention+Centre,+BKC,+Mumbai',
    },
  },
  {
    id: 'metro',
    title: 'Aqua Metro Line 3 & Suburban Rail',
    badge: 'Walking Distance • 350m',
    badgeBg: 'bg-[#FAF5EA]',
    badgeBorder: 'border-[#E6C687]',
    badgeText: 'text-[#785613]',
    icon: Train,
    iconBg: 'bg-[#FAF5EA]',
    iconBorder: 'border-[#E6C687]/60',
    iconText: 'text-[#785613]',
    summary: 'BKC Metro Station access, plus direct links to Bandra (WR) and Kurla (CR) rail stations.',
    expandedDetails: {
      route: 'Underground Aqua Line 3 directly connects Airport (CSMIA) to BKC Station.',
      travelTime: '3–4 min sheltered pedestrian walk from BKC Metro Station Exit 2 to Gate 3.',
      tips: 'Suburban rail commuters can take frequent 8-min feeder autos/buses from Bandra (Western) or Kurla (Central).',
      directionsUrl:
        'https://www.google.com/maps/dir/BKC+Metro+Station,+Mumbai/Jio+World+Convention+Centre,+BKC,+Mumbai',
    },
  },
  {
    id: 'roads',
    title: 'Arterial Roads & On-Site Parking',
    badge: 'Multi-Level P1/P2 Basements',
    badgeBg: 'bg-slate-100',
    badgeBorder: 'border-slate-200',
    badgeText: 'text-slate-700',
    icon: Car,
    iconBg: 'bg-slate-100',
    iconBorder: 'border-slate-200',
    iconText: 'text-slate-700',
    summary: 'Multi-lane access from BKC main avenues with dedicated multi-level parking at JWCC.',
    expandedDetails: {
      route: 'Direct avenue entry off BKC Main Avenue & Bandra-Kurla Link Road.',
      travelTime: '2,000+ vehicle capacity with dedicated EV charging bays on Basement Level P1.',
      tips: 'VIP & Speaker drop-off porch located at Gate 4. Visitor self-parking entry via Gate 6 Ramp.',
      directionsUrl:
        'https://www.google.com/maps/search/?api=1&query=Jio+World+Convention+Centre+Parking+Mumbai',
    },
  },
];

export const VenueMapSection: React.FC = () => {
  const [activeTransit, setActiveTransit] = useState<TransitMode>('airport');
  const [mapMode, setMapMode] = useState<MapViewMode>('live');
  const [mapLayer, setMapLayer] = useState<MapLayer>('roadmap');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [copiedCoords, setCopiedCoords] = useState(false);

  // Compute live Google Maps embed URL based on active state
  const getEmbedUrl = () => {
    const layerParam = mapLayer === 'satellite' ? '&t=k' : '&t=m';
    if (activeTransit === 'airport') {
      return `https://maps.google.com/maps?saddr=Chhatrapati+Shivaji+Maharaj+International+Airport+Mumbai&daddr=Jio+World+Convention+Centre+Mumbai${layerParam}&z=13&output=embed`;
    }
    if (activeTransit === 'metro') {
      return `https://maps.google.com/maps?q=BKC+Metro+Station+Mumbai+to+Jio+World+Convention+Centre${layerParam}&z=16&output=embed`;
    }
    if (activeTransit === 'roads') {
      return `https://maps.google.com/maps?q=Jio+World+Convention+Centre+G+Block+BKC+Mumbai${layerParam}&z=16&output=embed`;
    }
    return `https://maps.google.com/maps?q=Jio+World+Convention+Centre+Mumbai${layerParam}&z=15&output=embed`;
  };

  const handleCopyCoords = async () => {
    try {
      await navigator.clipboard.writeText('19.0628, 72.8687');
      setCopiedCoords(true);
      setTimeout(() => setCopiedCoords(false), 2200);
    } catch {
      // Fallback
      setCopiedCoords(true);
      setTimeout(() => setCopiedCoords(false), 2200);
    }
  };

  const currentTransit = TRANSIT_OPTIONS.find((t) => t.id === activeTransit) || TRANSIT_OPTIONS[0];

  return (
    <section id="logistics" className="space-y-6 scroll-mt-24 relative">
      <span id="venue" className="scroll-mt-24 absolute -top-24 pointer-events-none" />

      {/* Section Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 pb-3 border-b border-slate-200/80">
        <div className="space-y-1">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#0A3D27]/10 border border-[#0A3D27]/20 text-[#0A3D27] text-[11px] font-bold">
            <MapPin className="w-3 h-3 text-[#0A3D27]" />
            <span>Flagship Venue • BKC Mumbai</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
            Venue & Arrival Logistics
          </h2>
        </div>
        <p className="text-xs text-slate-500 max-w-md sm:text-right">
          Jio World Convention Centre (JWCC), Pavilion 3 — situated at the center of Mumbai's Bandra Kurla Complex (BKC).
        </p>
      </div>

      {/* Master Cockpit Container */}
      <div
        className={`bg-white border border-slate-200/90 rounded-3xl shadow-xs overflow-hidden transition-all duration-300 ${
          isFullscreen
            ? 'fixed inset-4 z-50 shadow-2xl flex flex-col overflow-y-auto bg-white/95 backdrop-blur-md'
            : ''
        }`}
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 divide-y lg:divide-y-0 lg:divide-x divide-slate-100">
          
          {/* Left Column: Venue Identity & Interactive Transit Hub (5 cols) */}
          <div className="lg:col-span-5 p-6 sm:p-7 flex flex-col justify-between space-y-6">
            <div className="space-y-4">
              
              {/* Facility Badges */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-bold text-[#785613] bg-[#FAF5EA] border border-[#E6C687] px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                  Premier International Facility
                </span>
                <span className="text-[10px] font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                  Pavilion 3
                </span>
              </div>

              {/* Title & Description */}
              <div className="space-y-1.5">
                <h3 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
                  Jio World Convention Centre (JWCC)
                </h3>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                  India's preeminent convention hub offering 161,000 sq. meters of world-class exhibition space,
                  robust high-speed 5G connectivity, advanced HVAC filtration, and dedicated freight docks.
                </p>
              </div>

              {/* Key Spec Badges Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 text-xs pt-1">
                <div className="p-3 rounded-2xl bg-slate-50/80 border border-slate-100 space-y-1">
                  <div className="flex items-center gap-1.5 text-slate-500 text-[11px] font-semibold">
                    <MapPin className="w-3.5 h-3.5 text-[#0A3D27]" />
                    <span>Address</span>
                  </div>
                  <div className="font-medium text-slate-900 text-xs leading-snug">
                    Pavilion 3, JWCC, BKC, Mumbai 400051
                  </div>
                </div>

                <div className="p-3 rounded-2xl bg-slate-50/80 border border-slate-100 space-y-1">
                  <div className="flex items-center gap-1.5 text-slate-500 text-[11px] font-semibold">
                    <Clock className="w-3.5 h-3.5 text-[#0A3D27]" />
                    <span>Hours</span>
                  </div>
                  <div className="font-medium text-slate-900 text-xs leading-snug">
                    27–28 July 2027<br />
                    <span className="text-slate-500 text-[11px]">10:00 AM – 06:00 PM</span>
                  </div>
                </div>

                <div className="p-3 rounded-2xl bg-slate-50/80 border border-slate-100 space-y-1 relative group">
                  <div className="flex items-center justify-between text-slate-500 text-[11px] font-semibold">
                    <span className="flex items-center gap-1.5">
                      <Navigation className="w-3.5 h-3.5 text-[#0A3D27]" />
                      <span>Geolocation</span>
                    </span>
                    <button
                      type="button"
                      onClick={handleCopyCoords}
                      title="Copy GPS coordinates"
                      className="p-1 hover:bg-slate-200/60 rounded-md transition-colors text-slate-500 hover:text-slate-800 cursor-pointer focus:outline-hidden"
                    >
                      {copiedCoords ? (
                        <Check className="w-3 h-3 text-emerald-600" />
                      ) : (
                        <Copy className="w-3 h-3" />
                      )}
                    </button>
                  </div>
                  <div className="font-mono text-slate-900 text-[11px] font-medium leading-snug">
                    19.0628° N<br />
                    72.8687° E
                  </div>
                  {copiedCoords && (
                    <span className="absolute -top-6 right-2 text-[10px] font-bold bg-[#0A3D27] text-white px-2 py-0.5 rounded shadow-sm animate-fade-in">
                      Copied GPS!
                    </span>
                  )}
                </div>
              </div>

              {/* Interactive Transit Hub Section */}
              <div className="pt-2 space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold text-[#0A3D27] uppercase tracking-wider flex items-center gap-1.5">
                    <Compass className="w-3.5 h-3.5 text-[#0A3D27]" />
                    <span>Transit Routes & Corridors</span>
                  </span>
                  <span className="text-[11px] text-slate-500 font-medium">
                    Click to view route on map
                  </span>
                </div>

                {/* Transit Mode Cards (Interactive) */}
                <div className="space-y-2">
                  {TRANSIT_OPTIONS.map((item) => {
                    const isSelected = activeTransit === item.id;
                    const IconComponent = item.icon;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => {
                          setActiveTransit(item.id);
                        }}
                        className={`w-full text-left p-3.5 rounded-2xl border transition-all cursor-pointer focus:outline-hidden ${
                          isSelected
                            ? 'bg-emerald-950/5 border-[#0A3D27] shadow-sm ring-1 ring-[#0A3D27]/20'
                            : 'bg-white border-slate-200/80 hover:border-slate-300 hover:bg-slate-50/60'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div
                            className={`w-9 h-9 rounded-xl border flex items-center justify-center shrink-0 mt-0.5 transition-colors ${
                              isSelected
                                ? 'bg-[#0A3D27] border-[#0A3D27] text-[#FAF5EA]'
                                : `${item.iconBg} ${item.iconBorder} ${item.iconText}`
                            }`}
                          >
                            <IconComponent className="w-4 h-4" />
                          </div>

                          <div className="min-w-0 flex-1 space-y-1">
                            <div className="flex items-center justify-between gap-2">
                              <h5
                                className={`text-xs font-bold truncate ${
                                  isSelected ? 'text-[#0A3D27]' : 'text-slate-900'
                                }`}
                              >
                                {item.title}
                              </h5>
                              <span
                                className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 border ${item.badgeBg} ${item.badgeBorder} ${item.badgeText}`}
                              >
                                {item.badge}
                              </span>
                            </div>

                            <p className="text-[11px] text-slate-600 leading-relaxed line-clamp-1">
                              {item.summary}
                            </p>

                            {/* Expanded Details when active */}
                            {isSelected && (
                              <div className="pt-2 mt-2 border-t border-emerald-900/10 space-y-1.5 text-[11px] text-slate-700 animate-fade-in">
                                <div className="flex items-center gap-1.5 text-slate-600">
                                  <Route className="w-3.5 h-3.5 text-[#0A3D27] shrink-0" />
                                  <span className="font-semibold text-slate-900">
                                    {item.expandedDetails.route}
                                  </span>
                                </div>
                                <div className="text-[11px] text-slate-500 bg-white/80 p-2 rounded-xl border border-slate-100">
                                  💡 <span className="font-medium text-slate-700">{item.expandedDetails.tips}</span>
                                </div>
                                <div className="pt-1 flex items-center gap-2">
                                  <a
                                    href={item.expandedDetails.directionsUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#0A3D27] hover:text-[#062B1B] hover:underline"
                                  >
                                    <span>Get Turn-by-Turn Navigation</span>
                                    <ExternalLink className="w-3 h-3" />
                                  </a>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

            </div>

            {/* Bottom Actions Bar */}
            <div className="pt-3 border-t border-slate-100 flex flex-wrap items-center gap-2.5">
              <a
                href="https://maps.google.com/?q=Jio+World+Convention+Centre+Mumbai"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3.5 py-2.5 bg-[#0A3D27] hover:bg-[#062B1B] text-white text-xs font-bold rounded-xl transition-all shadow-md shadow-emerald-950/15 cursor-pointer"
              >
                <Navigation className="w-3.5 h-3.5" />
                <span>Open in Google Maps</span>
                <ExternalLink className="w-3 h-3 opacity-70" />
              </a>

              <a
                href={eventInfo.brochureUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3.5 py-2.5 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 text-xs font-bold rounded-xl transition-all shadow-2xs cursor-pointer"
              >
                <Download className="w-3.5 h-3.5 text-[#0A3D27]" />
                <span>Venue Specs (PDF)</span>
                <ExternalLink className="w-3 h-3 text-slate-400" />
              </a>

              <Link
                to="/my-iiab"
                className="inline-flex items-center gap-1.5 text-xs font-bold text-[#0A3D27] hover:underline sm:ml-auto"
              >
                <span>Hotel booking rates</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>

          {/* Right Column: Modern Interactive Map Cockpit (7 cols) */}
          <div className="lg:col-span-7 bg-slate-900 relative flex flex-col min-h-[480px] lg:min-h-[560px]">
            
            {/* Map Top Control Header Bar */}
            <div className="p-3.5 sm:p-4 bg-slate-950/90 backdrop-blur-md border-b border-slate-800/80 flex flex-wrap items-center justify-between gap-2.5 z-10">
              
              {/* Mode Switcher Tabs */}
              <div className="flex items-center gap-1 p-1 bg-slate-900 border border-slate-800 rounded-xl">
                <button
                  type="button"
                  onClick={() => setMapMode('live')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer focus:outline-hidden ${
                    mapMode === 'live'
                      ? 'bg-[#0A3D27] text-white shadow-xs'
                      : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
                  }`}
                >
                  <MapIcon className="w-3.5 h-3.5 text-[#E6C687]" />
                  <span>Interactive Map</span>
                </button>

                <button
                  type="button"
                  onClick={() => setMapMode('corridors')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer focus:outline-hidden ${
                    mapMode === 'corridors'
                      ? 'bg-[#0A3D27] text-white shadow-xs'
                      : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
                  }`}
                >
                  <Route className="w-3.5 h-3.5 text-[#E6C687]" />
                  <span>Transit Corridors</span>
                  <span className="hidden sm:inline-block text-[9px] font-bold bg-[#FAF5EA] text-[#785613] px-1.5 py-0.2 rounded-sm uppercase">
                    Schematic
                  </span>
                </button>
              </div>

              {/* Right Map Actions (Satellite, Fullscreen, External) */}
              <div className="flex items-center gap-2">
                {mapMode === 'live' && (
                  <button
                    type="button"
                    onClick={() => setMapLayer(mapLayer === 'roadmap' ? 'satellite' : 'roadmap')}
                    className="px-2.5 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 border border-slate-800 text-[11px] font-semibold text-slate-300 hover:text-white transition-all flex items-center gap-1.5 cursor-pointer focus:outline-hidden"
                    title="Toggle Roadmap / Satellite View"
                  >
                    <Layers className="w-3 h-3 text-[#E6C687]" />
                    <span className="capitalize">{mapLayer === 'roadmap' ? 'Satellite' : 'Roadmap'}</span>
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  className="p-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 border border-slate-800 text-slate-300 hover:text-white transition-all cursor-pointer focus:outline-hidden"
                  title={isFullscreen ? 'Exit Full Screen' : 'Expand Full Screen'}
                >
                  {isFullscreen ? (
                    <Minimize2 className="w-4 h-4 text-emerald-400" />
                  ) : (
                    <Maximize2 className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Main Map Viewport Area */}
            <div className="relative flex-1 w-full h-full min-h-[420px] overflow-hidden bg-slate-950">
              
              {/* MODE 1: Interactive Live Map Embed */}
              {mapMode === 'live' && (
                <div className="w-full h-full relative">
                  <iframe
                    title="Jio World Convention Centre Interactive Map"
                    src={getEmbedUrl()}
                    className="w-full h-full border-0 filter contrast-105"
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                  />

                  {/* Floating Glassmorphic Venue Callout Badge */}
                  <div className="absolute top-3 left-3 z-10 max-w-[280px] bg-slate-950/85 backdrop-blur-md border border-emerald-500/40 rounded-2xl p-3 shadow-xl pointer-events-auto">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="relative flex h-2.5 w-2.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                      </span>
                      <span className="text-[11px] font-bold text-white tracking-wide">
                        JWCC • Pavilion 3
                      </span>
                      <span className="text-[9px] font-bold text-[#E6C687] bg-[#FAF5EA]/10 border border-[#E6C687]/40 px-1.5 py-0.2 rounded ml-auto">
                        Venue Hub
                      </span>
                    </div>

                    <div className="text-[10px] text-slate-300 leading-snug">
                      G Block, Bandra Kurla Complex (BKC), Mumbai
                    </div>

                    <div className="mt-2 pt-2 border-t border-slate-800/80 flex items-center justify-between text-[10px]">
                      <span className="text-slate-400">Selected Route:</span>
                      <span className="font-semibold text-emerald-300 truncate max-w-[130px]">
                        {currentTransit.title}
                      </span>
                    </div>
                  </div>

                  {/* Floating Bottom Navigation Shortcut Pill */}
                  <div className="absolute bottom-3 right-3 z-10 flex items-center gap-2">
                    <a
                      href={currentTransit.expandedDetails.directionsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1.5 bg-[#0A3D27] hover:bg-[#062B1B] text-white text-[11px] font-bold rounded-xl shadow-lg border border-emerald-500/30 flex items-center gap-1.5 transition-all cursor-pointer"
                    >
                      <Navigation className="w-3 h-3 text-[#E6C687]" />
                      <span>Direct Navigation</span>
                      <ExternalLink className="w-2.5 h-2.5 opacity-80" />
                    </a>
                  </div>
                </div>
              )}

              {/* MODE 2: Visual Transit Corridors Schematic */}
              {mapMode === 'corridors' && (
                <div className="w-full h-full p-4 sm:p-6 flex flex-col justify-between bg-gradient-to-br from-[#06180F] via-[#091F14] to-[#040C07] text-white select-none">
                  
                  {/* Schematic Instructions */}
                  <div className="flex items-center justify-between text-[11px] pb-2 border-b border-emerald-900/40">
                    <span className="flex items-center gap-1.5 text-emerald-300 font-bold">
                      <Sparkles className="w-3.5 h-3.5 text-[#E6C687]" />
                      <span>BKC Gateway Transit Corridors</span>
                    </span>
                    <span className="text-slate-400 text-[10px]">
                      Click corridors to highlight routes
                    </span>
                  </div>

                  {/* Vector Schematic Layout */}
                  <div className="my-auto py-4 relative">
                    
                    {/* SVG Connector Lines */}
                    <svg
                      className="absolute inset-0 w-full h-full pointer-events-none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      {/* Airport to JWCC Line */}
                      <path
                        d="M 120 40 Q 180 120 280 160"
                        fill="none"
                        stroke={activeTransit === 'airport' ? '#E6C687' : '#059669'}
                        strokeWidth={activeTransit === 'airport' ? '3' : '2'}
                        strokeDasharray={activeTransit === 'airport' ? '6,3' : '4,4'}
                        className="transition-all duration-300"
                      />
                      {/* Metro to JWCC Line */}
                      <path
                        d="M 440 50 Q 380 110 320 160"
                        fill="none"
                        stroke={activeTransit === 'metro' ? '#E6C687' : '#06B6D4'}
                        strokeWidth={activeTransit === 'metro' ? '3' : '2'}
                        strokeDasharray={activeTransit === 'metro' ? '6,3' : '4,4'}
                        className="transition-all duration-300"
                      />
                      {/* Roads to JWCC Line */}
                      <path
                        d="M 120 270 Q 200 230 280 190"
                        fill="none"
                        stroke={activeTransit === 'roads' ? '#E6C687' : '#94A3B8'}
                        strokeWidth={activeTransit === 'roads' ? '3' : '2'}
                        strokeDasharray={activeTransit === 'roads' ? '6,3' : '4,4'}
                        className="transition-all duration-300"
                      />
                    </svg>

                    {/* Nodes Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 relative z-10">
                      
                      {/* Node: Airport T2 */}
                      <div
                        onClick={() => setActiveTransit('airport')}
                        className={`p-3 rounded-2xl border transition-all cursor-pointer ${
                          activeTransit === 'airport'
                            ? 'bg-emerald-950/80 border-[#E6C687] shadow-lg shadow-black/40 ring-1 ring-[#E6C687]/50'
                            : 'bg-slate-900/60 border-slate-800/80 hover:border-slate-700'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="flex items-center gap-1.5 text-xs font-bold text-white">
                            <Plane className="w-3.5 h-3.5 text-emerald-400" />
                            <span>Airport T2</span>
                          </span>
                          <span className="text-[10px] text-emerald-300 font-semibold bg-emerald-950 px-2 py-0.5 rounded-full border border-emerald-800/60">
                            15–20 min
                          </span>
                        </div>
                        <div className="text-[10px] text-slate-300">
                          BKC Connector Expressway • 8 km direct link
                        </div>
                      </div>

                      {/* Node: Aqua Metro Line 3 */}
                      <div
                        onClick={() => setActiveTransit('metro')}
                        className={`p-3 rounded-2xl border transition-all cursor-pointer ${
                          activeTransit === 'metro'
                            ? 'bg-emerald-950/80 border-[#E6C687] shadow-lg shadow-black/40 ring-1 ring-[#E6C687]/50'
                            : 'bg-slate-900/60 border-slate-800/80 hover:border-slate-700'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="flex items-center gap-1.5 text-xs font-bold text-white">
                            <Train className="w-3.5 h-3.5 text-cyan-400" />
                            <span>Aqua Line 3 Metro</span>
                          </span>
                          <span className="text-[10px] text-[#E6C687] font-semibold bg-amber-950/40 px-2 py-0.5 rounded-full border border-[#E6C687]/30">
                            350m walk
                          </span>
                        </div>
                        <div className="text-[10px] text-slate-300">
                          BKC Station • Direct links to Bandra & Kurla
                        </div>
                      </div>
                    </div>

                    {/* Central Target Node: JWCC Pavilion 3 */}
                    <div className="my-5 p-4 rounded-2xl bg-gradient-to-r from-emerald-950 via-[#0A3D27] to-emerald-950 border-2 border-[#E6C687] shadow-2xl relative z-20 text-center">
                      <div className="inline-flex items-center gap-2 mb-1 px-3 py-0.5 rounded-full bg-black/40 border border-[#E6C687]/40 text-[#E6C687] text-[10px] font-bold">
                        <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                        <span>DESTINATION CONCOURSE</span>
                      </div>
                      <h4 className="text-base font-black text-white tracking-wide">
                        Jio World Convention Centre • Pavilion 3
                      </h4>
                      <p className="text-[11px] text-emerald-200 mt-1">
                        Bandra Kurla Complex (BKC), Mumbai • Dedicated Delegate Turnstiles
                      </p>

                      {/* Gate Callouts */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3 pt-3 border-t border-emerald-800/50 text-[10px]">
                        <div className="bg-black/30 p-1.5 rounded-xl border border-white/5">
                          <div className="text-slate-400">Gate 3</div>
                          <div className="font-bold text-white">Delegate Entry</div>
                        </div>
                        <div className="bg-black/30 p-1.5 rounded-xl border border-white/5">
                          <div className="text-slate-400">Gate 4</div>
                          <div className="font-bold text-[#E6C687]">VIP Porch</div>
                        </div>
                        <div className="bg-black/30 p-1.5 rounded-xl border border-white/5 col-span-2 sm:col-span-1">
                          <div className="text-slate-400">Basements P1/P2</div>
                          <div className="font-bold text-white">Visitor Parking</div>
                        </div>
                      </div>
                    </div>

                    {/* Bottom Node: Road Arteries */}
                    <div
                      onClick={() => setActiveTransit('roads')}
                      className={`p-3 rounded-2xl border transition-all cursor-pointer relative z-10 ${
                        activeTransit === 'roads'
                          ? 'bg-emerald-950/80 border-[#E6C687] shadow-lg shadow-black/40 ring-1 ring-[#E6C687]/50'
                          : 'bg-slate-900/60 border-slate-800/80 hover:border-slate-700'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="flex items-center gap-1.5 text-xs font-bold text-white">
                          <Car className="w-3.5 h-3.5 text-slate-300" />
                          <span>Western & Eastern Expressways</span>
                        </span>
                        <span className="text-[10px] text-slate-300 font-semibold bg-slate-800 px-2 py-0.5 rounded-full border border-slate-700">
                          2,000+ Parking Bays
                        </span>
                      </div>
                      <div className="text-[10px] text-slate-300">
                        Multi-lane arterial access into G Block with dedicated EV charging
                      </div>
                    </div>
                  </div>

                  {/* Schematic Footer Details */}
                  <div className="pt-2 border-t border-emerald-900/40 flex items-center justify-between text-[10px] text-slate-400">
                    <span className="flex items-center gap-1">
                      <Info className="w-3 h-3 text-[#E6C687]" />
                      <span>Official Venue Partner: Jio World Centre, BKC</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => setMapMode('live')}
                      className="text-[#E6C687] hover:underline font-bold cursor-pointer"
                    >
                      Switch to Live Map →
                    </button>
                  </div>
                </div>
              )}

            </div>

            {/* Bottom Status & Info Bar */}
            <div className="px-4 py-2 bg-slate-950 border-t border-slate-800/60 flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-400">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                <span className="text-slate-300 font-medium">
                  {mapMode === 'live' ? 'Live GPS Location' : 'Corridor Transit Schematic'}
                </span>
                <span className="text-slate-600">•</span>
                <span className="font-mono text-slate-400">19.0628° N, 72.8687° E</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-slate-400">Official Venue Partner</span>
                <span className="font-semibold text-slate-200">Jio World Centre, BKC</span>
              </div>
            </div>

          </div>

        </div>

        {/* ================================================================= */}
        {/* RESTORED VENUE LOGISTICS: HOTELS, SETUP TIMINGS & SAFETY RULES    */}
        {/* ================================================================= */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* 1. BKC Nearby Delegate Hotels (7 cols) */}
          <div className="lg:col-span-7 bg-white border border-slate-200 rounded-3xl p-6 sm:p-7 shadow-xs space-y-4">
            <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-3">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-[#785613]">
                  Hospitality &amp; Stay
                </span>
                <h3 className="text-base font-black text-slate-900 flex items-center gap-2 mt-0.5">
                  <Building className="w-4 h-4 text-[#0A3D27]" />
                  <span>BKC Nearby Delegate Hotels</span>
                </h3>
              </div>
              <span className="text-[11px] font-semibold text-emerald-800 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200">
                Walking / 5–10 Min Drive
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 text-xs">
              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="font-black text-slate-900">Trident BKC</span>
                  <span className="text-[10px] font-bold text-[#0A3D27] bg-emerald-100/80 px-2 py-0.5 rounded-full">
                    400m
                  </span>
                </div>
                <p className="text-[11px] text-slate-600 leading-relaxed">
                  C-56, G Block, Bandra Kurla Complex • 5-minute walk to JWCC Pavilion 3 Gate 3.
                </p>
                <span className="text-[10px] font-semibold text-[#785613] block pt-1">
                  5-Star Luxury Business Hotel
                </span>
              </div>

              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="font-black text-slate-900">Sofitel Mumbai BKC</span>
                  <span className="text-[10px] font-bold text-[#0A3D27] bg-emerald-100/80 px-2 py-0.5 rounded-full">
                    600m
                  </span>
                </div>
                <p className="text-[11px] text-slate-600 leading-relaxed">
                  C-57, G Block, Bandra Kurla Complex • Direct corporate shuttle &amp; pedestrian walkway.
                </p>
                <span className="text-[10px] font-semibold text-[#785613] block pt-1">
                  5-Star International Flagship
                </span>
              </div>

              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="font-black text-slate-900">Grand Hyatt Mumbai</span>
                  <span className="text-[10px] font-bold text-[#0A3D27] bg-emerald-100/80 px-2 py-0.5 rounded-full">
                    3.2 km
                  </span>
                </div>
                <p className="text-[11px] text-slate-600 leading-relaxed">
                  Off Western Express Highway, Santacruz East • 10-minute drive via BKC Connector.
                </p>
                <span className="text-[10px] font-semibold text-[#785613] block pt-1">
                  Preferred Group Delegation Hub
                </span>
              </div>
            </div>
          </div>

          {/* 2. Exhibitor Setup & Dismantle Timings + Safety Guidelines (5 cols) */}
          <div className="lg:col-span-5 bg-[#FAF5EA] border border-[#E6C687] rounded-3xl p-6 sm:p-7 shadow-xs space-y-4">
            <div className="border-b border-[#E6C687]/60 pb-3">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#785613]">
                Exhibitor &amp; Venue Operations
              </span>
              <h3 className="text-base font-black text-slate-900 flex items-center gap-2 mt-0.5">
                <Clock className="w-4 h-4 text-[#0A3D27]" />
                <span>Setup, Dismantle &amp; Safety Guidelines</span>
              </h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div className="p-3.5 rounded-2xl bg-white border border-[#E6C687]/80 space-y-1">
                <span className="text-[10px] font-bold uppercase text-slate-500 block">
                  Exhibitor Build-Up
                </span>
                <span className="font-black text-slate-900 block">26 July 2027</span>
                <span className="text-[11px] text-[#0A3D27] font-semibold block">
                  Raw: 08:00 AM · Shell: 02:00 PM
                </span>
              </div>

              <div className="p-3.5 rounded-2xl bg-white border border-[#E6C687]/80 space-y-1">
                <span className="text-[10px] font-bold uppercase text-slate-500 block">
                  Stall Dismantle
                </span>
                <span className="font-black text-slate-900 block">28 July 2027 · 06:00 PM</span>
                <span className="text-[11px] text-[#785613] font-semibold block">
                  Freight Gate 5 clearance till 11:30 PM
                </span>
              </div>
            </div>

            <div className="p-3.5 rounded-2xl bg-white/90 border border-[#E6C687]/60 text-[11px] text-slate-700 space-y-1.5">
              <div className="font-bold text-slate-900 flex items-center gap-1.5">
                <Info className="w-3.5 h-3.5 text-[#0A3D27]" />
                <span>Pavilion 3 Venue Safety &amp; Access Rules</span>
              </div>
              <ul className="list-disc list-inside space-y-0.5 text-slate-600">
                <li>Government-issued Photo ID + 6-char Collection Code required at Registration Desk.</li>
                <li>High-visibility vests &amp; closed footwear mandatory during 26 July build-up hours.</li>
                <li>100% fire-retardant stall fabrication &amp; certified 3-phase DB connections enforced by JWCC.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VenueMapSection;
